import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import CrmUpgrade from "./CrmUpgrade";
import { crmFetch, formatDate, dueLabel, calendarDate } from "./crmApi";
import "./Crm.css";

/**
 * Follow-ups: the studio's to-do list, grouped by urgency so the morning
 * glance answers "who do I need to call today?".
 */
function CrmFollowUps() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [showDone, setShowDone] = useState(false);
  const [planBlocked, setPlanBlocked] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/tasks");
      setTasks(d.tasks || []);
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const g = { overdue: [], today: [], upcoming: [], someday: [], done: [] };
    for (const t of tasks) {
      if (t.status === "done") { g.done.push(t); continue; }
      if (!t.due_date) { g.someday.push(t); continue; }
      const due = calendarDate(t.due_date);
      if (!due) { g.someday.push(t); continue; }
      due.setHours(0, 0, 0, 0);
      if (due < today) g.overdue.push(t);
      else if (due.getTime() === today.getTime()) g.today.push(t);
      else g.upcoming.push(t);
    }
    return g;
  }, [tasks]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await crmFetch("/api/crm/tasks", {
        method: "POST",
        body: { title, due_date: dueDate || null },
      });
      setTitle("");
      setDueDate("");
      load();
    } catch (err) {
      window.alert(err.message);
    }
  };

  const toggle = async (task) => {
    const status = task.status === "done" ? "open" : "done";
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try {
      await crmFetch(`/api/crm/tasks/${task.id}`, { method: "PATCH", body: { status } });
    } catch { /* reload settles it */ }
    load();
  };

  const remove = async (task) => {
    try {
      await crmFetch(`/api/crm/tasks/${task.id}`, { method: "DELETE" });
      load();
    } catch (err) {
      window.alert(err.message);
    }
  };

  const renderGroup = (key, label, icon, items) => {
    if (items.length === 0) return null;
    return (
      <div className="crm-task-group" key={key}>
        <h3 className={`crm-task-group-title ${key}`}>{icon} {label} ({items.length})</h3>
        {items.map((t) => {
          const due = dueLabel(t.due_date);
          return (
            <div key={t.id} className="crm-task-row">
              <button
                className={`crm-task-check ${t.status === "done" ? "done" : ""}`}
                onClick={() => toggle(t)}
                title={t.status === "done" ? "Reopen" : "Mark done"}
              >
                {t.status === "done" ? "✓" : ""}
              </button>
              <div className={`crm-task-title ${t.status === "done" ? "done" : ""}`}>
                {t.title}
                <div className="crm-task-sub">
                  {t.client_name && <span>👤 {t.client_name} · </span>}
                  {t.lead_event_type && <span>📸 {t.lead_event_type} · </span>}
                  {t.due_date ? formatDate(t.due_date) : "No date"}
                  {due && t.status !== "done" && (
                    <span className={`crm-due ${due.tone}`}> · {due.text}</span>
                  )}
                </div>
              </div>
              <button className="crm-task-del" onClick={() => remove(t)} title="Delete">🗑</button>
            </div>
          );
        })}
      </div>
    );
  };

  const openCount = tasks.filter((t) => t.status === "open").length;

  return (
    <div className="crm-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="crm-main">
        <header className="crm-header">
          <div className="crm-header-left">
            <button className="crm-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <div>
              <h2>✅ Follow-ups</h2>
              <p className="crm-header-sub">
                {openCount === 0 ? "All caught up" : `${openCount} open follow-up${openCount === 1 ? "" : "s"}`}
              </p>
            </div>
          </div>
        </header>

        <div className="crm-content">
          {planBlocked ? <CrmUpgrade /> : (
          <>
          <form className="crm-quick-add" onSubmit={addTask}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="＋ Add a follow-up… e.g. Send the wedding quote to Priya"
            />
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <button className="crm-btn primary" type="submit">Add</button>
          </form>

          {loading ? (
            <div className="crm-loading">Loading follow-ups…</div>
          ) : tasks.length === 0 ? (
            <div className="crm-empty">
              <span className="crm-empty-icon">🌤️</span>
              Nothing to follow up on.
              <div className="crm-empty-hint">
                Add one above, or set a follow-up date on a lead — it shows on the Overview too.
              </div>
            </div>
          ) : (
            <>
              {renderGroup("overdue", "Overdue", "🔴", groups.overdue)}
              {renderGroup("today", "Today", "🟠", groups.today)}
              {renderGroup("upcoming", "Upcoming", "🔵", groups.upcoming)}
              {renderGroup("someday", "No date", "⚪", groups.someday)}
              {groups.done.length > 0 && (
                <button
                  className="crm-link-btn"
                  style={{ marginBottom: 10 }}
                  onClick={() => setShowDone(!showDone)}
                >
                  {showDone ? "Hide" : "Show"} completed ({groups.done.length})
                </button>
              )}
              {showDone && renderGroup("done", "Completed", "✅", groups.done)}
            </>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CrmFollowUps;
